import { LoaderCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AnimeWall, LoadingWall, RequestErrorWall } from '../components/content-wall';
import { Select } from '../components/select';
import { useApp } from '../components/app-context';
import { t } from '../i18n';
import { $showNsfw } from '../store/app.store';
import type { AnimeSubject, SubjectPage } from '../types/bgm';

interface SearchFilters {
  type: string;
  region: string;
  source: string;
  rating: string;
  sort: string;
}

const initialFilters: SearchFilters = { type: '', region: '', source: '', rating: '', sort: 'heat' };

export const SearchPage = ({ query }: { query: string }) => {
  const { bgm, showToast } = useApp();
  const [filters, setFilters] = useState(initialFilters);
  const [subjects, setSubjects] = useState<AnimeSubject[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(true);
  const [failed, setFailed] = useState(false);
  const offset = useRef(0);
  const loadingRef = useRef(false);
  const sentinel = useRef<HTMLDivElement>(null);

  const load = useCallback(
    async (reset = false) => {
      const keyword = query.trim();
      if (!keyword || loadingRef.current || (!available && !reset)) {
        return;
      }
      if (reset) {
        offset.current = 0;
        setSubjects([]);
        setAvailable(true);
        setFailed(false);
      }
      loadingRef.current = true;
      setLoading(true);
      try {
        const limit = 20;
        const metaTags = filters.type ? [filters.type] : [];
        const tags = [filters.region, filters.source].filter(Boolean);
        const result = await bgm<SubjectPage>(`/v0/search/subjects?limit=${limit}&offset=${offset.current}`, {
          method: 'POST',
          body: JSON.stringify({
            keyword,
            sort: filters.sort,
            filter: {
              type: [2],
              ...(metaTags.length ? { meta_tags: metaTags } : {}),
              ...(tags.length ? { tag: tags } : {}),
              ...(filters.rating ? { rating: [`>=${filters.rating}`] } : {}),
              ...(!$showNsfw.get() ? { nsfw: false } : {}),
            },
          }),
        });
        offset.current += result.data.length;
        setFailed(false);
        setTotal(result.total);
        setAvailable(result.data.length > 0 && offset.current < result.total);
        setSubjects((current) => {
          const known = new Set(reset ? [] : current.map((subject) => subject.id));
          return [...(reset ? [] : current), ...result.data.filter((subject) => !known.has(subject.id))];
        });
      } catch (error) {
        setAvailable(false);
        setFailed(true);
        showToast(error instanceof Error ? error.message : String(error), 'error');
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [available, bgm, filters, query, showToast],
  );

  useEffect(() => {
    void load(true);
  }, [filters, query]);

  useEffect(() => {
    const node = sentinel.current;
    if (!node) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void load();
        }
      },
      { rootMargin: '500px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [load]);

  const update = (name: keyof SearchFilters, value: string): void => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  return (
    <section>
      <div className="hero-copy compact-hero">
        <p className="eyebrow">BANGUMI SEARCH</p>
        <h1>{t('searchResults')}</h1>
        <p>{total ? `${query} · ${total}` : query}</p>
      </div>
      <form className="filter-panel">
        <Filter
          label={t('animeType')}
          value={filters.type}
          onChange={(value) => update('type', value)}
          options={[
            [t('noLimit'), ''],
            ['TV', 'TV'],
            [t('movie'), '剧场版'],
            ['OVA', 'OVA'],
            ['WEB', 'WEB'],
          ]}
        />
        <Filter
          label={t('region')}
          value={filters.region}
          onChange={(value) => update('region', value)}
          options={[
            [t('noLimit'), ''],
            [t('japan'), '日本'],
            [t('china'), '中国'],
            [t('western'), '欧美'],
          ]}
        />
        <Filter
          label={t('source')}
          value={filters.source}
          onChange={(value) => update('source', value)}
          options={[
            [t('noLimit'), ''],
            [t('original'), '原创'],
            [t('mangaAdaptation'), '漫画改'],
            [t('novelAdaptation'), '小说改'],
            [t('gameAdaptation'), '游戏改'],
          ]}
        />
        <Filter
          label={t('rating')}
          value={filters.rating}
          onChange={(value) => update('rating', value)}
          options={[
            [t('noLimit'), ''],
            ['6+', '6'],
            ['7+', '7'],
            ['8+', '8'],
            ['9+', '9'],
          ]}
        />
        <Filter
          label={t('sort')}
          value={filters.sort}
          onChange={(value) => update('sort', value)}
          options={[
            [t('sortHeat'), 'heat'],
            [t('sortScore'), 'score'],
            [t('sortRank'), 'rank'],
            [t('sortMatch'), 'match'],
          ]}
        />
      </form>
      {loading && !subjects.length ? (
        <LoadingWall />
      ) : failed && !subjects.length ? (
        <RequestErrorWall onRetry={() => void load(true)} />
      ) : (
        <AnimeWall subjects={subjects} />
      )}
      <div className={`scroll-sentinel${available ? '' : ' hidden'}`} ref={sentinel}>
        {loading ? <LoaderCircle className="spin" /> : null}
      </div>
    </section>
  );
};

const Filter = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) => (
  <label className="grid gap-1.5">
    <span className="m-0 block text-[11px] font-black text-(--muted)">{label}</span>
    <Select
      value={value}
      ariaLabel={label}
      onChange={onChange}
      options={options.map(([name, optionValue]) => ({ label: name, value: optionValue }))}
    />
  </label>
);
